import { ChevronLeft, ChevronRight } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";

export interface PaginationProps {
  page: number;
  pages: number;
  onChange: (page: number) => void;
}

const ARROW = "grid size-(--control-sm) cursor-pointer place-items-center rounded-sm border border-green-700 text-green-800 enabled:hover:bg-green-50 disabled:cursor-not-allowed disabled:border-paper-3 disabled:text-ink-300";

/** Page control: first, last and the pages either side of the current one. */
export function Pagination({ page, pages, onChange }: PaginationProps) {
  const go = (n: number) => onChange(Math.max(1, Math.min(pages, n)));
  const list: (number | "gap")[] = [];
  for (let n = 1; n <= pages; n++) {
    if (n === 1 || n === pages || Math.abs(n - page) <= 1) list.push(n);
    else if (list[list.length - 1] !== "gap") list.push("gap");
  }
  return (
    <nav aria-label="Pages" className="flex items-center gap-1">
      <button type="button" aria-label="Previous page" className={ARROW} disabled={page <= 1} onClick={() => go(page - 1)}>
        <Icon icon={ChevronLeft} size={18} />
      </button>
      {list.map((n, index) =>
        n === "gap" ? (
          <span key={`gap${index}`} className="w-8 text-center type-small text-text-muted">
            …
          </span>
        ) : (
          <button
            key={n}
            type="button"
            aria-current={n === page ? "page" : undefined}
            onClick={() => go(n)}
            className={cx(
              "size-8 cursor-pointer rounded-sm border type-label [font-size:var(--text-sm)] [transition:var(--transition-control)]",
              n === page ? "border-green-700 bg-green-700 text-white" : "border-border-default bg-surface-card text-ink-700",
            )}
          >
            {n}
          </button>
        ),
      )}
      <button type="button" aria-label="Next page" className={ARROW} disabled={page >= pages} onClick={() => go(page + 1)}>
        <Icon icon={ChevronRight} size={18} />
      </button>
    </nav>
  );
}
